import React from 'react';
import {buildSourceViewerPayload} from './utils/sourceFileSelection';
import {removeSourcesFile, SourceDeletionChoiceRequiredError} from './sourcesApi';
import {clearSourcesCache} from './sourcesCacheAdapter';
import {removeItemsFromSources} from './sourcesListMutations';
import {
    useSourcesExplorerActions,
    useSourcesViewerActions,
    useSourcesViewerState,
} from './useSourcesSectionStoreSelectors';

const useSourcesFileActions = ({
    reportId,
    activeView,
    currentFolderId,
    currentViewKey,
    sources,
    userId,
    reloadActiveViewSilently,
}) => {
    const {selectedFileId} = useSourcesViewerState();
    const {isFileViewerOpen, openFileViewer, setViewerFile, closeFileViewer} = useSourcesViewerActions();
    const {setError, setSources} = useSourcesExplorerActions();

    const [filePendingDelete, setFilePendingDelete] = React.useState(null);
    const [fileDeletionConflict, setFileDeletionConflict] = React.useState(null);
    const [isDeletingFile, setIsDeletingFile] = React.useState(false);
    const [fileDeleteError, setFileDeleteError] = React.useState(null);

    const applyLocalFileRemoval = React.useCallback((fileId) => {
        setSources(removeItemsFromSources(sources, {fileIds: [fileId]}));
        clearSourcesCache(activeView).catch(() => {});
    }, [activeView, setSources, sources]);

    const handleFileClick = React.useCallback((file) => {
        const payload = buildSourceViewerPayload({
            reportId,
            file,
            sourceFolderId: currentFolderId,
            sourceViewKey: currentViewKey,
        });

        if (isFileViewerOpen) {
            setViewerFile(payload);
            return;
        }

        openFileViewer(payload);
    }, [currentFolderId, currentViewKey, isFileViewerOpen, openFileViewer, reportId, setViewerFile]);

    const handleFileDeleteRequest = React.useCallback((file) => {
        if (!file?.id) {
            return;
        }
        setFilePendingDelete(file);
        setFileDeletionConflict(null);
        setFileDeleteError(null);
    }, []);

    const handleCloseFileDeleteDialog = React.useCallback(() => {
        if (isDeletingFile) {
            return;
        }
        setFilePendingDelete(null);
        setFileDeletionConflict(null);
        setFileDeleteError(null);
    }, [isDeletingFile]);

    const handleFileDelete = React.useCallback(async (purgeReferencedCanonical) => {
        const file = filePendingDelete;
        if (!file?.id) {
            return;
        }
        if (!userId) {
            setFileDeleteError('An authenticated user is required to remove files.');
            return;
        }

        setIsDeletingFile(true);
        setFileDeleteError(null);
        try {
            await removeSourcesFile({
                fileId: file.id,
                userId,
                purgeReferencedCanonical,
            });

            if (String(selectedFileId) === String(file.id)) {
                closeFileViewer();
            }

            applyLocalFileRemoval(file.id);
            setError(null);
            setFilePendingDelete(null);
            setFileDeletionConflict(null);
            reloadActiveViewSilently().catch(() => {});
        } catch (deleteError) {
            if (deleteError instanceof SourceDeletionChoiceRequiredError) {
                setFileDeletionConflict(deleteError.details || null);
                return;
            }
            console.error('[SourcesSection] Failed to delete file:', deleteError);
            setFileDeleteError(deleteError?.message || 'Failed to delete file.');
        } finally {
            setIsDeletingFile(false);
        }
    }, [
        applyLocalFileRemoval,
        closeFileViewer,
        filePendingDelete,
        reloadActiveViewSilently,
        selectedFileId,
        setError,
        userId,
    ]);

    return {
        filePendingDelete,
        fileDeletionConflict,
        fileDeleteError,
        isDeletingFile,
        handleFileClick,
        handleFileDeleteRequest,
        handleCloseFileDeleteDialog,
        handleFileDelete,
    };
};

export default useSourcesFileActions;
